/**
 * Shared MRZ generation utility for both frontend and admin previews
 * Used by DirectEditCardPreview (storefront) and CardTestPreview (admin)
 */

/**
 * Generate MRZ Line 1 from name data
 * Format: IDD<<LASTNAME<<FIRSTNAME<<<<<<<<<<<<<<<<<<< (44 chars)
 */
export function generateMrzLine1(
  firstName: string,
  lastName: string,
  targetLength: number = 44
): string {
  const cleanName = (name: string) => name.toUpperCase().replace(/[^A-Z]/g, '');
  const fn = cleanName(firstName || 'JOHN');
  const ln = cleanName(lastName || 'DOE');
  
  const prefix = 'IDD<<';
  const namePart = `${prefix}${ln}<<${fn}`;
  return namePart.padEnd(targetLength, '<').substring(0, targetLength);
}

/**
 * Generate MRZ Line 2 from form data
 * Format: IDNUMBER<YYMMDD<SEX<YYMMDD<<<<<<<<<<<<<<<0 (44 chars)
 */
export function generateMrzLine2(
  idNumber: string,
  dateOfBirth: string,
  expiryDate: string,
  sex: string = 'M',
  targetLength: number = 44
): string {
  // Parse DD.MM.YYYY -> YYMMDD
  const parseDate = (d: string): string => {
    const match = d.match(/(\d{2})\.(\d{2})\.(\d{4})/);
    if (match) {
      return match[3].slice(2) + match[2] + match[1]; // YYMMDD
    }
    return '900815'; // Fallback
  };
  
  const id = (idNumber || 'AB12345678').toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 10).padEnd(10, '<');
  const dob = parseDate(dateOfBirth || '15.08.1990');
  const exp = parseDate(expiryDate || '01.01.2034');
  const sexChar = (sex || 'M').toUpperCase().charAt(0);
  
  const line = `${id}<${dob}<${sexChar}<${exp}`;
  // Ensure exactly targetLength with trailing 0 for checksum
  return line.padEnd(targetLength - 1, '<').substring(0, targetLength - 1) + '0';
}

/**
 * Get MRZ text for a specific field key from form data
 * This generates the correct MRZ line based on available form data
 * Default 150 chars - excess will be cut off by overflow:hidden in preview
 */
export function getMrzText(
  fieldKey: string,
  formData: Record<string, string>,
  targetLength: number = 150
): string {
  // Extract data from form with fallbacks
  const firstName = formData['firstName'] || formData['first_name'] || formData['vorname'] || 'JOHN';
  const lastName = formData['lastName'] || formData['last_name'] || formData['nachname'] || 'SMITH';
  const idNumber = formData['idNumber'] || formData['id_number'] || formData['idnumber'] || 'AB12345678';
  const dob = formData['dateOfBirth'] || formData['date_of_birth'] || formData['dob'] || '15.08.1990';
  const expiry = formData['expiryDate'] || formData['expiry_date'] || '01.01.2034';
  const sex = formData['sex'] || formData['gender'] || 'M';
  
  const key = fieldKey.toLowerCase();
  
  if (key.includes('mrzline1') || key.includes('mrz_line_1')) {
    return generateMrzLine1(firstName, lastName, targetLength);
  } else if (key.includes('mrzline2') || key.includes('mrz_line_2')) {
    return generateMrzLine2(idNumber, dob, expiry, sex, targetLength);
  }
  
  return '';
}

/**
 * Check if a field key represents an MRZ field
 */
export function isMrzFieldKey(fieldKey: string): boolean {
  const key = fieldKey.toLowerCase();
  return key.includes('mrz');
}
